/* eslint-disable max-lines */
import Konva from 'konva';
import { RefObject, useMemo } from 'react';
import { Arrow, Line, Path, Text } from 'react-konva';
import { Point } from './types';
import {
  AreaZoneType,
  getPolygonIconPlacement,
  getZoneIconScale,
  getZonePatternImage,
  lineDirectionArrow,
  lineLabelBox,
  PRESENCE_LINE_COLOR,
  ZONE_HALO_STROKE_COLOR,
  ZONE_ICON_CONFIGS,
  ZONE_PATTERN_CONFIGS,
} from './utils';

const LABEL_WIDTH = 160;
const LABEL_FONT_SIZE = 13;

interface ShapeProps {
  points: Point[];
  flatPoints: number[];
  label?: string;
  isSelected: boolean;
  /** The main stroke; the drag handlers move its points imperatively */
  lineRef: RefObject<Konva.Line>;
}

interface LabelProps {
  /** Left edge of the label box */
  x: number;
  y: number;
  text: string;
  align?: 'left' | 'center' | 'right';
}

function ShapeLabel({ x, y, text, align = 'center' }: LabelProps) {
  return (
    <Text
      x={x}
      y={y}
      width={LABEL_WIDTH}
      align={align}
      text={text}
      fontSize={LABEL_FONT_SIZE}
      fontStyle="bold"
      fill="#ffffff"
      shadowColor="rgba(0, 0, 0, 0.7)"
      shadowBlur={4}
      listening={false}
    />
  );
}

/** A filled zone: patterned polygon, centred type icon, name under it. */
export function AreaShape({
  type,
  points,
  flatPoints,
  label,
  isSelected,
  lineRef,
}: ShapeProps & { type: AreaZoneType }) {
  const patternConfig = ZONE_PATTERN_CONFIGS[type];
  const iconConfig = ZONE_ICON_CONFIGS[type];
  const { center, radius } = useMemo(
    () => getPolygonIconPlacement(points),
    [points]
  );
  const iconScale = useMemo(() => getZoneIconScale(radius), [radius]);

  return (
    <>
      {/* Outer halo stroke — subtle white edge for contrast on dark backgrounds */}
      <Line
        points={flatPoints}
        stroke={ZONE_HALO_STROKE_COLOR}
        strokeWidth={isSelected ? 3 : 2}
        closed
        listening={false}
      />
      {/* Main zone: pattern fill + colored inner stroke. Konva accepts
          HTMLCanvasElement here at runtime, but its config type is narrowed
          to HTMLImageElement — cast accordingly. */}
      <Line
        ref={lineRef}
        points={flatPoints}
        fillPatternImage={
          getZonePatternImage(type) as unknown as HTMLImageElement
        }
        fillPatternRepeat="repeat"
        stroke={patternConfig.strokeColor}
        strokeWidth={isSelected ? 1.5 : 1}
        closed
      />
      {/* Centered zone type icon — placed at the polygon's pole of
          inaccessibility and sized to the inscribed circle, so it stays
          inside the shape even for irregular/concave polygons. */}
      {iconScale > 0 && (
        <Path
          x={center.x - 12 * iconScale}
          y={center.y - 12 * iconScale}
          data={iconConfig.path}
          fill={iconConfig.fill}
          opacity={iconConfig.opacity}
          scaleX={iconScale}
          scaleY={iconScale}
          listening={false}
        />
      )}
      {label && radius > 0 && (
        <ShapeLabel
          x={center.x - LABEL_WIDTH / 2}
          y={center.y + 12 * iconScale + 4}
          text={label}
        />
      )}
    </>
  );
}

/**
 * An open polyline with an arrow to the side an "ltr" crossing lands on.
 * A wide hit stroke makes a 3px line grabbable.
 */
export function LineShape({
  points,
  flatPoints,
  label,
  isSelected,
  lineRef,
}: ShapeProps) {
  const arrow = useMemo(() => lineDirectionArrow(points), [points]);

  return (
    <>
      <Line
        points={flatPoints}
        stroke={ZONE_HALO_STROKE_COLOR}
        strokeWidth={isSelected ? 6 : 5}
        lineCap="round"
        lineJoin="round"
        listening={false}
      />
      <Line
        ref={lineRef}
        points={flatPoints}
        stroke={PRESENCE_LINE_COLOR}
        strokeWidth={isSelected ? 3 : 2.5}
        lineCap="round"
        lineJoin="round"
        hitStrokeWidth={18}
      />
      {arrow && (
        <Arrow
          points={[arrow.from.x, arrow.from.y, arrow.to.x, arrow.to.y]}
          stroke={PRESENCE_LINE_COLOR}
          fill={PRESENCE_LINE_COLOR}
          strokeWidth={2.5}
          pointerLength={8}
          pointerWidth={8}
          shadowColor="rgba(0, 0, 0, 0.6)"
          shadowBlur={3}
          listening={false}
        />
      )}
      {label && arrow && (
        <ShapeLabel
          {...lineLabelBox(arrow, LABEL_WIDTH, LABEL_FONT_SIZE)}
          text={label}
        />
      )}
    </>
  );
}
